import Link from 'next/link';

import {
  getLocalizedMarketingPathname,
  resolveLocaleOrDefault,
} from '@kit/i18n';
import { Button } from '@kit/ui/button';
import { LanguageSelector } from '@kit/ui/language-selector';
import { Trans } from '@kit/ui/trans';

import { cn } from '../../lib/utils';

interface FooterSection {
  heading: React.ReactNode;
  links: Array<{
    href: string;
    label: React.ReactNode;
  }>;
}

interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  logo: React.ReactNode;
  description: React.ReactNode;
  copyright: React.ReactNode;
  sections: FooterSection[];
  currentLanguage: string;
  signUpPath: string;
}

export const Footer: React.FC<FooterProps> = ({
  className,
  logo,
  description,
  copyright,
  sections,
  currentLanguage,
  signUpPath,
  ...props
}) => {
  const locale = resolveLocaleOrDefault(currentLanguage);

  return (
    <footer
      className={cn(
        'site-footer relative mt-auto w-full bg-secondary',
        className,
      )}
      {...props}
    >
      <div className="container py-16">
        {/* Logo */}
        <div className="flex flex-col items-center space-y-8 lg:flex-row lg:space-y-0">
          <div className="lg:1/2 flex w-full space-x-2">
            <div className="flex flex-col space-y-4">
              <div>{logo}</div>
              <div className="flex flex-col space-y-4">
                <p className="text-md max-w-sm font-medium">{description}</p>
              </div>
            </div>
          </div>

          {/* Nav / Button */}
          <div className="flex w-full flex-col space-y-8 lg:flex-row lg:justify-end lg:space-x-6 lg:space-y-0 xl:space-x-16">
            <div>
              <Button asChild variant={'default'} size={'lg'}>
                <Link href={signUpPath}>
                  <Trans i18nKey={'marketing:getStarted'} />
                </Link>
              </Button>
            </div>
            {sections.map((section, index) => (
              <div key={index}>
                <div className="flex flex-col space-y-2.5">
                  <FooterSectionHeading>{section.heading}</FooterSectionHeading>

                  <FooterSectionList>
                    {section.links.map((link, linkIndex) => (
                      <FooterLink key={linkIndex} href={link.href}>
                        {link.label}
                      </FooterLink>
                    ))}
                  </FooterSectionList>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer bar */}
      <div className="border-t border-t-white/25 py-5">
        <div className="container">
          <div className="flex flex-col space-y-5 lg:flex-row lg:items-center lg:space-x-8 lg:space-y-0">
            <div className="mr-auto">
              <LanguageSelector
                classNameTrigger={
                  'border-none p-0 !outline-none !shadow-none !ring-0'
                }
              ></LanguageSelector>
            </div>
            <div className="line-height-[1em] flex flex-col gap-y-5 md:flex-row md:items-center md:space-x-5 md:space-y-0">
              {/* Social */}
              <div className="flex items-center gap-x-5">
                <a
                  href="https://www.linkedin.com/company/narrafix"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <img
                    src="/images/linkedin-white.svg"
                    alt="LinkedIn"
                    className="h-6 w-auto"
                  />
                </a>
                <a
                  href="https://www.instagram.com/narrafix.audio/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <img
                    src="/images/instagram-white.svg"
                    alt="Instagram"
                    className="h-6 w-auto"
                  />
                </a>
                <a
                  href="https://whatsapp.com/channel/0029Vb6B8ruDTkK05joYU23G"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                >
                  <img
                    src="/images/whatsapp-white.svg"
                    alt="WhatsApp"
                    className="h-6 w-auto"
                  />
                </a>
              </div>
              {/* Help Center and Legal */}
              <div className="flex items-center gap-x-5">
                <a
                  href="https://help.narrafix.audio"
                  target="_blank"
                  rel="noreferrer"
                  className="whitespace-nowrap font-medium"
                >
                  <Trans i18nKey={'marketing:helpCenter'} />
                </a>
                <Link
                  href={getLocalizedMarketingPathname('/imprint', locale)}
                  className="whitespace-nowrap font-medium"
                >
                  <Trans i18nKey={'marketing:imprint'} />
                </Link>
              </div>
              {/* Data Privacy and Terms of Service */}
              <div className="flex items-center gap-x-5">
                <Link
                  href={getLocalizedMarketingPathname('/data-privacy', locale)}
                  className="whitespace-nowrap font-medium"
                >
                  <Trans i18nKey={'marketing:dataPrivacy'} />
                </Link>
                <Link
                  href={getLocalizedMarketingPathname('/terms-of-service', locale)}
                  className="whitespace-nowrap font-medium"
                >
                  <Trans i18nKey={'marketing:termsOfService'} />
                </Link>
              </div>
            </div>
            <div className="whitespace-nowrap font-medium">{copyright}</div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function FooterSectionHeading(props: React.PropsWithChildren) {
  return <span className="font-heading">{props.children}</span>;
}

function FooterSectionList(props: React.PropsWithChildren) {
  return <ul className="flex flex-col space-y-2.5">{props.children}</ul>;
}

function FooterLink({
  href,
  children,
}: React.PropsWithChildren<{ href: string }>) {
  return (
    <li className="text-sm text-muted-foreground hover:underline [&>a]:transition-colors">
      <a href={href}>{children}</a>
    </li>
  );
}
